// constants.js — Market data tables, cost defaults, scenario presets for projection engine

/** Per-market configuration for frontier/EM T-bill projection */
export const MARKET_CONFIG = {
  Kenya:          { ccy: 'KES', longRunYield: 0.11, whtRate: 0.15, historicalShock: -0.20, shockLabel: 'KES -20% (2023)' },
  India:          { ccy: 'INR', longRunYield: 0.07, whtRate: 0.10, historicalShock: -0.12, shockLabel: 'INR -12% (2013)' },
  Brazil:         { ccy: 'BRL', longRunYield: 0.12, whtRate: 0.15, historicalShock: -0.30, shockLabel: 'BRL -30% (2020)' },
  Mexico:         { ccy: 'MXN', longRunYield: 0.10, whtRate: 0.10, historicalShock: -0.20, shockLabel: 'MXN -20% (2016)' },
  Turkey:         { ccy: 'TRY', longRunYield: 0.30, whtRate: 0.10, historicalShock: -0.45, shockLabel: 'TRY -45% (2021-23)' },
  Bangladesh:     { ccy: 'BDT', longRunYield: 0.08, whtRate: 0.15, historicalShock: -0.15, shockLabel: 'BDT -15% (2024)' },
  'South Africa': { ccy: 'ZAR', longRunYield: 0.08, whtRate: 0.15, historicalShock: -0.15, shockLabel: 'ZAR -15% (2015)' },
  Egypt:          { ccy: 'EGP', longRunYield: 0.20, whtRate: 0.20, historicalShock: -0.40, shockLabel: 'EGP -40% (2024)' },
  Ethiopia:       { ccy: 'ETB', longRunYield: 0.18, whtRate: 0.05, historicalShock: -0.40, shockLabel: 'ETB -40% (2024)' },
};

/** Benchmark yields by base currency */
export const BENCHMARK_CONFIG = {
  USD: { label: 'US T-bill', yield: 0.050 },
  GBP: { label: 'UK Gilt', yield: 0.045 },
  CHF: { label: 'Swiss Govt', yield: 0.015 },
  AED: { label: 'UAE T-bill', yield: 0.050 },
  JPY: { label: 'JGB', yield: 0.001 },
  EUR: { label: 'Bund', yield: 0.030 },
};

/** Cost defaults in basis points */
export const COST_DEFAULTS = {
  platformFeeBps: 25,
  fxSpreadBps: 15,
  forwardSpreadBps: 10,
  custodyBps: 5,
};

/** Profit-taking / reinvestment modes */
export const PROFIT_TAKING_MODES = {
  full_reinvest: {
    label: 'Full Reinvestment',
    description: 'All maturities and income reinvested into new T-bills',
  },
  interest_only: {
    label: 'Interest Only',
    description: 'Withdraw interest income, reinvest principal at maturity',
  },
  annual_withdraw: {
    label: 'Annual Withdrawal',
    description: 'Withdraw a fixed percentage or amount each year',
  },
  amortise: {
    label: 'Amortising',
    description: 'Return equal portions of capital + interest each year',
  },
};

/** Scenario presets */
export const SCENARIO_PRESETS = {
  base: {
    label: 'Base',
    description: 'Yields mean-revert to long-run levels; foreign exchange follows uncovered interest parity',
  },
  optimistic: {
    label: 'Optimistic',
    description: 'Yields stay flat at current levels; foreign exchange remains stable',
  },
  adverse: {
    label: 'Adverse',
    description: 'Yields spike +300bps in shock year; historical foreign exchange devaluation replays; 3-month repatriation delay',
  },
};

/** Projection horizon in years */
export const PROJECTION_HORIZON = 10;

/** Mean-reversion speed (fraction of gap closed per year) */
export const YIELD_MEAN_REVERSION_SPEED = 0.25;

/** Adverse scenario: yield spike in bps */
export const ADVERSE_YIELD_SPIKE_BPS = 300;

/** Adverse scenario: default shock year (0-indexed) */
export const ADVERSE_SHOCK_YEAR = 3;

/** Adverse scenario: repatriation delay in months */
export const ADVERSE_REPATRIATION_DELAY_MONTHS = 3;

/** Default reinvestment tenor in days */
export const DEFAULT_REINVESTMENT_TENOR = 364;

/** Available reinvestment tenors */
export const REINVESTMENT_TENORS = [
  { value: 91, label: '91-day' },
  { value: 182, label: '182-day' },
  { value: 364, label: '364-day' },
];

/** Default hedge ratio */
export const DEFAULT_HEDGE_RATIO = 0.75;

/** Words banned from any projection output (guardrail) */
export const BANNED_WORDS = ['will', 'guaranteed', 'expected return', 'forecast', 'promise', 'certain'];

/** Disclosure copy (requires legal review before release) */
export const DISCLOSURE_TEXT = [
  'This projection tool provides illustrative scenarios only and does not constitute investment advice, a forecast, or a guarantee of future performance.',
  'Past performance is not indicative of future results. Actual outcomes may differ materially from any scenario shown.',
  'All projections assume continuous reinvestment and do not account for market liquidity constraints, counterparty risk, or regulatory changes.',
  'Foreign exchange hedging costs and availability may vary significantly from the assumptions used.',
  'Withholding tax rates are subject to change based on applicable tax treaties and regulations.',
  'This tool is for educational and illustrative purposes only. Consult your financial advisor before making investment decisions.',
];
